import { useTranslation } from 'react-i18next';
import { CheckExpandable } from '@/components/CheckExpandable';
import { FloatExpandableCard } from '@/components/FloatExpandableCard';
import { SectionHeader } from '@/components/SectionHeader';
import { Button, Card } from '@arco-design/web-react';
import { IconPlus } from '@arco-design/web-react/icon';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { GrayBox, InsideWhiteBox, ItemDescription, ItemTitle } from '../tools';
import { HealthCheckItem, HealthCheckPannels } from './health-check-pannels';
import { HealthCheckData, useHealthCheckStore } from './useHealthCheckStore';

export function HealthCheck() {
  const { t } = useTranslation();

  const msg = {
    health_check: t('components:health.check'),
    health_check_description: t(
      'components:add.probes.to.periodically.check.container.health.status',
    ),
    alive_check: t('components:liveness.check'),
    alive_check_description: t('components:check.if.the.container.is.alive'),
    ready_check: t('components:readiness.check'),
    ready_check_description: t('components:check.if.the.container.can.handle.requests'),
    startup_check: t('components:startup.check'),
    startup_check_description: t('components:check.if.the.container.started.successfully'),
    add: t('common:add'),
  };

  const {
    isHealthcheckSelected,
    toggleHealthcheckSelected,
    validateHealthCheck,
    aliveCheck,
    readyCheck,
    startupCheck,
    updateAliveCheck,
    updateReadyCheck,
    updateStartupCheck,
    removeAliveCheck,
    removeReadyCheck,
    removeStartupCheck,
  } = useHealthCheckStore();

  // 收集的数据
  const [currentAliveCheck, setCurrentAliveCheck] = useState<HealthCheckData | undefined>(
    aliveCheck,
  );
  const [currentReadyCheck, setCurrentReadyCheck] = useState<HealthCheckData | undefined>(
    readyCheck,
  );
  const [currentStartupCheck, setCurrentStartupCheck] = useState<HealthCheckData | undefined>(
    startupCheck,
  );

  // 后续可以优化成互斥打开
  const [openAliveCheck, setOpenAliveCheck] = useState(false);
  const [openReadyCheck, setOpenReadyCheck] = useState(false);
  const [openStartupCheck, setOpenStartupCheck] = useState(false);

  // 是否打开配置面板
  const onToggleAliveCheck = (value?: boolean) => {
    if (value !== undefined) {
      setOpenAliveCheck(value);
    } else {
      setOpenAliveCheck((b) => !b);
    }
  };
  const onToggleReadyCheck = (value?: boolean) => {
    if (value !== undefined) {
      setOpenReadyCheck(value);
    } else {
      setOpenReadyCheck((b) => !b);
    }
  };
  const onToggleStartupCheck = (value?: boolean) => {
    if (value !== undefined) {
      setOpenStartupCheck(value);
    } else {
      setOpenStartupCheck((b) => !b);
    }
  };

  // 收集当前更改的数据
  const handleAliveCheckChange = useCallback((data: HealthCheckData) => {
    setCurrentAliveCheck(data);
  }, []);
  const handleReadyCheckChange = useCallback((data: HealthCheckData) => {
    setCurrentReadyCheck(data);
  }, []);
  const handleStartupCheckChange = useCallback((data: HealthCheckData) => {
    setCurrentStartupCheck(data);
  }, []);

  // 保存数据
  const onAliveCheckOk = async () => {
    const valid = await validateHealthCheck();
    if (valid) {
      updateAliveCheck(currentAliveCheck);
      onToggleAliveCheck(false);
    }
  };
  const onReadyCheckOk = async () => {
    const valid = await validateHealthCheck();
    if (valid) {
      updateReadyCheck(currentReadyCheck);
      onToggleReadyCheck(false);
    }
  };
  const onStartupCheckOk = async () => {
    const valid = await validateHealthCheck();
    if (valid) {
      updateStartupCheck(currentStartupCheck);
      onToggleStartupCheck(false);
    }
  };

  const Header = (
    <SectionHeader inline title={msg.health_check} description={msg.health_check_description} />
  );

  return (
    <>
      <Card>
        <CheckExpandable
          label={Header}
          checked={isHealthcheckSelected}
          onChange={toggleHealthcheckSelected}
        >
          <Content>
            <GrayBox>
              <ItemTitle>{msg.alive_check}</ItemTitle>
              <FloatExpandableCard
                content={
                  <HealthCheckPannels
                    disableSuccessThreshold
                    data={aliveCheck}
                    onChange={handleAliveCheckChange}
                  />
                }
                isOpen={openAliveCheck}
                onOk={onAliveCheckOk}
                onCancel={() => onToggleAliveCheck(false)}
              >
                <InsideWhiteBox>
                  {!aliveCheck ? (
                    <EmptyPlaceholder label={msg.add} onClick={onToggleAliveCheck} />
                  ) : (
                    <HealthCheckItem
                      data={aliveCheck}
                      onEditClick={() => {
                        onToggleAliveCheck(true);
                      }}
                      onDeleteClick={removeAliveCheck}
                    />
                  )}
                </InsideWhiteBox>
              </FloatExpandableCard>

              <ItemDescription>{msg.alive_check_description}</ItemDescription>
            </GrayBox>
            <GrayBox>
              <ItemTitle>{msg.ready_check}</ItemTitle>

              <FloatExpandableCard
                content={<HealthCheckPannels data={readyCheck} onChange={handleReadyCheckChange} />}
                isOpen={openReadyCheck}
                onOk={onReadyCheckOk}
                onCancel={() => onToggleReadyCheck(false)}
              >
                <InsideWhiteBox>
                  {!readyCheck ? (
                    <EmptyPlaceholder label={msg.add} onClick={onToggleReadyCheck} />
                  ) : (
                    <HealthCheckItem
                      data={readyCheck}
                      onEditClick={() => {
                        onToggleReadyCheck(true);
                      }}
                      onDeleteClick={removeReadyCheck}
                    />
                  )}
                </InsideWhiteBox>
              </FloatExpandableCard>

              <ItemDescription>{msg.ready_check_description}</ItemDescription>
            </GrayBox>
            <GrayBox>
              <ItemTitle>{msg.startup_check}</ItemTitle>

              <FloatExpandableCard
                content={
                  <HealthCheckPannels
                    disableSuccessThreshold
                    data={startupCheck}
                    onChange={handleStartupCheckChange}
                  />
                }
                isOpen={openStartupCheck}
                onOk={onStartupCheckOk}
                onCancel={() => onToggleStartupCheck(false)}
              >
                <InsideWhiteBox>
                  {!startupCheck ? (
                    <EmptyPlaceholder label={msg.add} onClick={onToggleStartupCheck} />
                  ) : (
                    <HealthCheckItem
                      data={startupCheck}
                      onEditClick={() => {
                        onToggleStartupCheck(true);
                      }}
                      onDeleteClick={removeStartupCheck}
                    />
                  )}
                </InsideWhiteBox>
              </FloatExpandableCard>

              <ItemDescription>{msg.startup_check_description}</ItemDescription>
            </GrayBox>
          </Content>
        </CheckExpandable>
      </Card>
    </>
  );
}

function EmptyPlaceholder({ label, onClick }: { label?: string; onClick?: () => void }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center' }}>
      <Button size="mini" type="primary" icon={<IconPlus />} onClick={onClick}>
        {label}
      </Button>
    </div>
  );
}

/** ============================ 样式 ==================================== */
const Content = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
